'use client';

import React from 'react';
import { useGeolocation } from '@/context/GeolocationContext';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ThirdPartyScriptsProps {
  gtmId?: string;
  nonce?: string;
}

// Default GTM ID - update this to match your actual GTM container ID
const DEFAULT_GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;

const subscribeNoop = () => () => {};

// Derive whether tracking is force-enabled via URL params (development only).
const getForceTrackingUrlSnapshot = (): boolean => {
  if (process.env.NODE_ENV !== 'development') {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('forceTracking') === '1' ||
      params.get('enableTracking') === '1'
    );
  } catch {
    return false;
  }
};

const GDPR_REGION_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
];

/**
 * Optimized third-party scripts component implementing Google Consent Mode v2
 * with region-specific default consent behavior:
 * 1. GDPR regions (EU/EEA): Default to 'denied', require explicit consent
 * 2. US and other regions: Default to 'granted', allow opt-out
 * 3. Always show banner for transparency and user control
 * 4. Update consent state when user accepts/rejects
 */
export const ThirdPartyScripts = ({
  gtmId = DEFAULT_GTM_ID,
  nonce,
}: ThirdPartyScriptsProps) => {
  // Use shared geolocation context to avoid duplicate API calls
  const { isGDPR: isGDPRRegion, isLoaded: regionLoaded } = useGeolocation();

  const gtmLoadedRef = React.useRef(false);
  const consentAppliedRef = React.useRef(false);
  const prePageviewQueuedRef = React.useRef(false);

  // Allow brief activation of tracking in development via env or URL param
  const forceTrackingEnv = process.env.NEXT_PUBLIC_FORCE_TRACKING === 'true';
  // Read the URL-based force-tracking flag in an SSR-safe way: false on the
  // server and first client render, then derived from the URL params (only in
  // development, matching the original effect-gated behavior).
  const forceTrackingUrl = React.useSyncExternalStore(
    subscribeNoop,
    getForceTrackingUrlSnapshot,
    () => false,
  );

  // Google Tag always loads in production (with consent mode)
  const isProduction = process.env.NODE_ENV === 'production';
  const prodLike = isProduction || forceTrackingEnv || forceTrackingUrl;
  const shouldLoadGoogleTag = prodLike && gtmId && regionLoaded;

  React.useEffect(() => {
    if (!shouldLoadGoogleTag) {
      return;
    }

    const win = window as typeof window & {
      dataLayer: any[];
      gtag?: (...args: any[]) => void;
      requestIdleCallback?: (
        cb: IdleRequestCallback,
        options?: IdleRequestOptions,
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    win.dataLayer = win.dataLayer || [];
    if (!win.gtag) {
      win.gtag = (...args: any[]) => {
        win.dataLayer.push(args);
      };
    }

    // Queue initial page view details so GTM processes them once loaded
    if (!prePageviewQueuedRef.current) {
      win.dataLayer.push({
        event: 'pre_gtm_page_view',
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
        timestamp: Date.now(),
      });
      prePageviewQueuedRef.current = true;
    }

    if (!consentAppliedRef.current) {
      const consentDefaults = isGDPRRegion
        ? {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            region: GDPR_REGION_CODES,
            wait_for_update: 500,
          }
        : {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
            analytics_storage: 'granted',
          };

      win.gtag('consent', 'default', consentDefaults);
      win.gtag('set', 'url_passthrough', true);
      win.gtag('set', 'ads_data_redaction', true);
      consentAppliedRef.current = true;
    }

    if (gtmLoadedRef.current) {
      return;
    }

    const startTime = performance.now();
    const MIN_DELAY = 1500; // ensure main bundle settles first
    const MAX_DELAY = 4000; // absolute latest we load GTM
    let idleId: number | undefined;
    let minDelayTimer: number | undefined;
    const listeners: Array<[keyof WindowEventMap, EventListener]> = [];

    const loadGtm = () => {
      if (gtmLoadedRef.current) {
        return;
      }
      gtmLoadedRef.current = true;

      win.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      script.setAttribute('data-gtm-loaded', 'true');
      script.setAttribute('fetchpriority', 'low');
      if (nonce) {
        // Set both the IDL property and content attribute so GTM can detect
        // and propagate the nonce to scripts it dynamically creates.
        // Browsers strip the content attribute after parse, but gtm.js reads
        // document.currentScript.nonce (the IDL property) during execution.
        script.nonce = nonce;
        script.setAttribute('nonce', nonce);
      }
      document.head.appendChild(script);
    };

    const cleanup = () => {
      listeners.forEach(([type, handler]) =>
        window.removeEventListener(type, handler),
      );
      if (typeof minDelayTimer === 'number') {
        window.clearTimeout(minDelayTimer);
      }
      window.clearTimeout(maxDelayTimer);
      if (
        typeof idleId === 'number' &&
        typeof win.cancelIdleCallback === 'function'
      ) {
        win.cancelIdleCallback(idleId);
      }
    };

    const triggerLoad = () => {
      if (gtmLoadedRef.current) {
        cleanup();
        return;
      }

      const elapsed = performance.now() - startTime;
      const remaining = MIN_DELAY - elapsed;

      if (remaining <= 0) {
        loadGtm();
        cleanup();
        return;
      }

      if (typeof minDelayTimer === 'number') {
        window.clearTimeout(minDelayTimer);
      }

      minDelayTimer = window.setTimeout(() => {
        loadGtm();
        cleanup();
      }, remaining);
    };

    const registerListener = (type: keyof WindowEventMap) => {
      const handler: EventListener = () => triggerLoad();
      window.addEventListener(type, handler, { once: true, passive: true });
      listeners.push([type, handler]);
    };

    ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach((type) =>
      registerListener(type as keyof WindowEventMap),
    );

    // Ensure GTM eventually loads even without interaction
    const maxDelayTimer = window.setTimeout(() => {
      loadGtm();
      cleanup();
    }, MAX_DELAY);

    // If the page fully loads, that's a good time to activate GTM
    const onWindowLoad = () => triggerLoad();
    window.addEventListener('load', onWindowLoad, {
      once: true,
      passive: true,
    });
    listeners.push(['load', onWindowLoad as EventListener]);

    if (typeof win.requestIdleCallback === 'function') {
      idleId = win.requestIdleCallback(triggerLoad, { timeout: MAX_DELAY });
    } else {
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(triggerLoad),
      );
    }

    return cleanup;
  }, [shouldLoadGoogleTag, gtmId, isGDPRRegion, nonce]);

  if (!shouldLoadGoogleTag) {
    return null;
  }

  return (
    <>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    </>
  );
};

// Default export for backward compatibility
export default ThirdPartyScripts;
